import { useMemo, useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, RotateCcw, Truck, Banknote, Package, DollarSign, Landmark, Building2 } from 'lucide-react';

const DEFAULT_CURRENCIES = ['USD', 'EUR', 'RUB', 'CNY'];
const DEFAULT_UNITS = ['т', 'шт', 'кг', 'л'];

const DEFAULTS = {
  price: '1000',
  volume: '20',
  // Импортный режим
  isImport: true,
  freight: '2000',
  postCustoms: '500',
  transit: '30',
  dutyRate: '5',
  vatRate: '20',
  customsFee: '150',
  moneyRate: '0.05',
  prepayPercent: '30',
  prepayDays: '5',
  delayPercent: '70',
  delayDays: '15',
  // Non-import режим (legacy)
  weight: '25',
  logistics: '1500',
};

/**
 * Калькулятор приведённой цены — 1C ERP версия.
 *
 * Режим Импорт (is_import=True):
 *   bank_fee   = price * 3%
 *   U          = (freight + post_customs) / volume
 *   customs    = ((price*volume + freight) * (1 + duty%)) * vat% + customs_fee
 *   customs_pu = customs / volume
 *   days_frozen = prepay_frac*(prepay_days + transit) + delay_frac*(transit - delay_days)
 *   E          = price * days_frozen * (money%/100) * (1 + vat%/100)
 *   Adjusted   = price + U + E + customs_pu + bank_fee
 *
 * Режим Закупка (не импорт): U=C/W, E=A*(pp*D*pd - dp*D*dd), Adjusted=A+U+E.
 *
 * Логика 100% совпадает с backend/math_engine.py.
 */
export default function PriceCalculator() {
  const [currency, setCurrency] = useState('USD');
  const [unit, setUnit] = useState('т');
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [units, setUnits] = useState(DEFAULT_UNITS);

  // Подтягиваем актуальные списки валют и единиц из админ-справочника
  // (с graceful fallback на дефолты при сбое сети).
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([api.get('/units'), api.get('/currencies')]);
        const ulist = Array.isArray(u.data) ? u.data.map(x => x.code).filter(Boolean) : [];
        const clist = Array.isArray(c.data) ? c.data.map(x => x.code).filter(Boolean) : [];
        if (ulist.length) setUnits(ulist);
        if (clist.length) setCurrencies(clist);
      } catch (_e) { /* fallback to defaults */ }
    })();
  }, []);
  const [price, setPrice] = useState(DEFAULTS.price);
  const [isImport, setIsImport] = useState(DEFAULTS.isImport);
  // Импортные поля
  const [volume, setVolume] = useState(DEFAULTS.volume);
  const [freight, setFreight] = useState(DEFAULTS.freight);
  const [postCustoms, setPostCustoms] = useState(DEFAULTS.postCustoms);
  const [transit, setTransit] = useState(DEFAULTS.transit);
  const [dutyRate, setDutyRate] = useState(DEFAULTS.dutyRate);
  const [vatRate, setVatRate] = useState(DEFAULTS.vatRate);
  const [customsFee, setCustomsFee] = useState(DEFAULTS.customsFee);
  // Стоимость денег
  const [moneyRate, setMoneyRate] = useState(DEFAULTS.moneyRate);
  const [prepayPercent, setPrepayPercent] = useState(DEFAULTS.prepayPercent);
  const [prepayDays, setPrepayDays] = useState(DEFAULTS.prepayDays);
  const [delayPercent, setDelayPercent] = useState(DEFAULTS.delayPercent);
  const [delayDays, setDelayDays] = useState(DEFAULTS.delayDays);
  // Non-import legacy
  const [weight, setWeight] = useState(DEFAULTS.weight);
  const [logistics, setLogistics] = useState(DEFAULTS.logistics);

  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const result = useMemo(() => {
    const A = num(price);
    const D = num(moneyRate) / 100;
    const ppPct = num(prepayPercent);
    const pp = ppPct / 100;
    const pd = num(prepayDays);
    const dpPct = num(delayPercent);
    const dp = dpPct / 100;
    const dd = num(delayDays);
    const prepayPlusDelay = ppPct + dpPct;
    const prepayDelayMismatch = Math.abs(prepayPlusDelay - 100) > 0.01;

    if (isImport) {
      const V = num(volume) || 1;
      const F = num(freight);
      const PC = num(postCustoms);
      const T = num(transit);
      const duty = num(dutyRate) / 100;
      const vat = num(vatRate) / 100;
      const cFee = num(customsFee);

      const bankFee = A * 0.03;
      const U = (F + PC) / V;

      const dynCustomsValue = A * V + F;
      const dutyAmount = dynCustomsValue * duty;
      const vatBase = dynCustomsValue + dutyAmount;
      const vatAmount = vatBase * vat;
      const totalCustoms = dutyAmount + vatAmount + cFee;
      const customsPerUnit = totalCustoms / V;

      const daysFrozen = pp * (pd + T) + dp * (T - dd);
      const baseE = A * daysFrozen * D;
      const E = baseE * (1 + vat);

      const adjusted = A + U + E + customsPerUnit + bankFee;
      return {
        A, U, E, bankFee, customsPerUnit, dutyAmount, vatAmount, cFee,
        daysFrozen, adjusted, prepayDelayMismatch, prepayPlusDelay,
        mode: 'import',
      };
    }

    // Non-import legacy
    const W = num(weight);
    const C = num(logistics);
    const U = (W > 0 && C > 0) ? C / W : 0;
    const ePrepay = A * pp * D * pd;
    const eDelay = A * dp * D * dd;
    const E = ePrepay - eDelay;
    const adjusted = A + U + E;
    return {
      A, U, E, ePrepay, eDelay, adjusted, prepayDelayMismatch, prepayPlusDelay,
      mode: 'legacy',
    };
  }, [price, isImport, volume, freight, postCustoms, transit, dutyRate, vatRate, customsFee,
      moneyRate, prepayPercent, prepayDays, delayPercent, delayDays, weight, logistics]);

  const reset = () => {
    setPrice(DEFAULTS.price);
    setIsImport(DEFAULTS.isImport);
    setVolume(DEFAULTS.volume);
    setFreight(DEFAULTS.freight);
    setPostCustoms(DEFAULTS.postCustoms);
    setTransit(DEFAULTS.transit);
    setDutyRate(DEFAULTS.dutyRate);
    setVatRate(DEFAULTS.vatRate);
    setCustomsFee(DEFAULTS.customsFee);
    setMoneyRate(DEFAULTS.moneyRate);
    setPrepayPercent(DEFAULTS.prepayPercent);
    setPrepayDays(DEFAULTS.prepayDays);
    setDelayPercent(DEFAULTS.delayPercent);
    setDelayDays(DEFAULTS.delayDays);
    setWeight(DEFAULTS.weight);
    setLogistics(DEFAULTS.logistics);
    setCurrency('USD');
    setUnit('т');
  };

  const fmt = (v) => (Number.isFinite(v) ? v.toFixed(2) : '0.00');
  const deltaFromA = result.adjusted - result.A;
  const deltaPct = result.A > 0 ? (deltaFromA / result.A) * 100 : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="price-calculator">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#0046AD]/10 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-[#0046AD]" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Калькулятор приведённой цены (1C ERP)</h2>
            <p className="text-xs text-slate-500 mt-0.5">Цена поставщика + Банк 3% + Логистика + Таможня + Стоимость денег</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={reset} data-testid="calc-reset-btn"
          className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Сбросить
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        {/* LEFT: inputs */}
        <div className="p-6 space-y-5">
          {/* Валюта / единица / режим импорта */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Валюта</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="calc-currency" className="rounded-sm border-slate-300 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Единица</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="calc-unit" className="rounded-sm border-slate-300 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Режим</Label>
              <label className="flex items-center gap-2 h-9 px-3 border border-slate-300 rounded-sm bg-white cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={isImport}
                  onChange={(e) => setIsImport(e.target.checked)}
                  data-testid="calc-is-import"
                  className="rounded border-slate-300"
                />
                <span className="text-slate-700">Импорт</span>
              </label>
            </div>
          </div>

          {/* Базовые: цена + объём */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
              <DollarSign className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">База</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Цена поставщика (A), {currency}/{unit}</Label>
                <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                  data-testid="calc-price" className="rounded-sm border-slate-300 tabular-nums h-9" />
              </div>
              {isImport && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Объём партии (V), {unit}</Label>
                  <Input type="number" step="0.01" value={volume} onChange={(e) => setVolume(e.target.value)}
                    data-testid="calc-volume" className="rounded-sm border-slate-300 tabular-nums h-9" />
                </div>
              )}
            </div>
          </div>

          {isImport ? (
            <>
              {/* Логистика импорт */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <Truck className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Логистика (на партию)</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Фрахт, {currency}</Label>
                    <Input type="number" step="0.01" value={freight} onChange={(e) => setFreight(e.target.value)}
                      data-testid="calc-freight" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">После таможни, {currency}</Label>
                    <Input type="number" step="0.01" value={postCustoms} onChange={(e) => setPostCustoms(e.target.value)}
                      data-testid="calc-post-customs" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Дни в пути</Label>
                    <Input type="number" step="1" value={transit} onChange={(e) => setTransit(e.target.value)}
                      data-testid="calc-transit" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                </div>
              </div>

              {/* Таможня */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <Landmark className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Таможня</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Пошлина, %</Label>
                    <Input type="number" step="0.01" value={dutyRate} onChange={(e) => setDutyRate(e.target.value)}
                      data-testid="calc-duty-rate" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">НДС, %</Label>
                    <Input type="number" step="0.01" value={vatRate} onChange={(e) => setVatRate(e.target.value)}
                      data-testid="calc-vat-rate" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Тамож. сбор, {currency}</Label>
                    <Input type="number" step="0.01" value={customsFee} onChange={(e) => setCustomsFee(e.target.value)}
                      data-testid="calc-customs-fee" className="rounded-sm border-slate-300 tabular-nums h-9" />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                <Truck className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Логистика (legacy)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Вес в конт. (W), {unit}</Label>
                  <Input type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)}
                    data-testid="calc-weight" className="rounded-sm border-slate-300 tabular-nums h-9" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">C, {currency}/конт.</Label>
                  <Input type="number" step="0.01" value={logistics} onChange={(e) => setLogistics(e.target.value)}
                    data-testid="calc-logistics" className="rounded-sm border-slate-300 tabular-nums h-9" />
                </div>
              </div>
            </div>
          )}

          {/* Стоимость денег */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
              <Banknote className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Стоимость денег</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Ставка (D), %/день</Label>
              <Input type="number" step="0.001" value={moneyRate} onChange={(e) => setMoneyRate(e.target.value)}
                data-testid="calc-money-rate" className="rounded-sm border-slate-300 tabular-nums h-9" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Предоплата, %</Label>
                <Input type="number" step="0.1" value={prepayPercent} onChange={(e) => setPrepayPercent(e.target.value)}
                  data-testid="calc-prepay-pct" className="rounded-sm border-slate-300 tabular-nums h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Предоплата, дней ДО</Label>
                <Input type="number" step="1" value={prepayDays} onChange={(e) => setPrepayDays(e.target.value)}
                  data-testid="calc-prepay-days" className="rounded-sm border-slate-300 tabular-nums h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Отсрочка, %</Label>
                <Input type="number" step="0.1" value={delayPercent} onChange={(e) => setDelayPercent(e.target.value)}
                  data-testid="calc-delay-pct" className="rounded-sm border-slate-300 tabular-nums h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Отсрочка, дней ПОСЛЕ</Label>
                <Input type="number" step="1" value={delayDays} onChange={(e) => setDelayDays(e.target.value)}
                  data-testid="calc-delay-days" className="rounded-sm border-slate-300 tabular-nums h-9" />
              </div>
            </div>
            {result.prepayDelayMismatch && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5"
                data-testid="calc-prepay-delay-warn">
                Предоплата + отсрочка = <span className="font-semibold tabular-nums">{result.prepayPlusDelay.toFixed(1)}%</span> (в реальной ставке должно быть 100%).
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: results */}
        <div className="p-6 bg-slate-50/60">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-3">Результат</div>

          <div className="bg-[#0046AD] text-white rounded-sm p-5 mb-4" data-testid="calc-adjusted-block">
            <div className="text-[11px] uppercase tracking-wider opacity-80 font-semibold">Приведённая цена</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums" data-testid="calc-adjusted-value">{fmt(result.adjusted)}</span>
              <span className="text-sm opacity-80">{currency}/{unit}</span>
            </div>
            <div className="mt-1 text-[11px] opacity-80 tabular-nums">
              Δ к цене поставщика: {deltaFromA >= 0 ? '+' : ''}{fmt(deltaFromA)} {currency} ({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(2)}%)
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
            <div className="flex items-center justify-between px-4 py-2.5" data-testid="calc-row-A">
              <div className="flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs text-slate-600">Цена поставщика <span className="text-slate-400">(A)</span></span>
              </div>
              <span className="text-sm font-semibold text-slate-800 tabular-nums">{fmt(result.A)} {currency}</span>
            </div>

            {result.mode === 'import' && (
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="calc-row-bank">
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-600">Банковская комиссия <span className="text-slate-400">(3%)</span></div>
                    <div className="text-[10px] text-slate-400 tabular-nums">{fmt(result.A)} × 3%</div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-slate-800 tabular-nums">+{fmt(result.bankFee)} {currency}</span>
              </div>
            )}

            <div className="flex items-center justify-between px-4 py-2.5" data-testid="calc-row-U">
              <div className="flex items-center gap-2">
                <Truck className="w-3.5 h-3.5 text-slate-400" />
                <div>
                  <div className="text-xs text-slate-600">Логистика <span className="text-slate-400">(U)</span></div>
                  <div className="text-[10px] text-slate-400 tabular-nums">
                    {result.mode === 'import'
                      ? `(${fmt(num(freight))} + ${fmt(num(postCustoms))}) ÷ ${fmt(num(volume))} ${unit}`
                      : `${fmt(num(logistics))} ÷ ${fmt(num(weight))} ${unit}`}
                  </div>
                </div>
              </div>
              <span className="text-sm font-semibold text-slate-800 tabular-nums">+{fmt(result.U)} {currency}</span>
            </div>

            {result.mode === 'import' && (
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="calc-row-customs">
                <div className="flex items-center gap-2">
                  <Landmark className="w-3.5 h-3.5 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-600">Таможня (за единицу)</div>
                    <div className="text-[10px] text-slate-400 tabular-nums">
                      Пошлина {fmt(result.dutyAmount)} + НДС {fmt(result.vatAmount)} + Сбор {fmt(result.cFee)} ÷ {fmt(num(volume))}
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-slate-800 tabular-nums">+{fmt(result.customsPerUnit)} {currency}</span>
              </div>
            )}

            <div className="flex items-center justify-between px-4 py-2.5" data-testid="calc-row-E">
              <div className="flex items-center gap-2">
                <Banknote className="w-3.5 h-3.5 text-slate-400" />
                <div>
                  <div className="text-xs text-slate-600">Стоимость денег <span className="text-slate-400">(E)</span></div>
                  <div className="text-[10px] text-slate-400 tabular-nums">
                    {result.mode === 'import'
                      ? `дней заморожено ${fmt(result.daysFrozen)} × ставка × (1 + НДС)`
                      : `предоплата +${fmt(result.ePrepay || 0)} − отсрочка ${fmt(result.eDelay || 0)}`}
                  </div>
                </div>
              </div>
              <span className={`text-sm font-semibold tabular-nums ${result.E > 0 ? 'text-rose-600' : result.E < 0 ? 'text-emerald-600' : 'text-slate-800'}`}>
                {result.E >= 0 ? '+' : ''}{fmt(result.E)} {currency}
              </span>
            </div>

            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-[#0046AD]" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  {result.mode === 'import' ? 'Итого (A + Bank + U + Customs + E)' : 'Итого (A + U + E)'}
                </span>
              </div>
              <span className="text-sm font-bold text-[#0046AD] tabular-nums">{fmt(result.adjusted)} {currency}</span>
            </div>
          </div>

          <div className="mt-4 text-[10px] text-slate-400 leading-relaxed">
            {result.mode === 'import' ? (
              <>Формула 1C ERP: <span className="tabular-nums">Adjusted = A + A·3% + (Фрахт+После)/V + ((A·V+Фрахт)·(1+П%))·НДС% + Сбор/V + A·дней·D·(1+НДС%)</span>, где дней = pp·(pd+T) + dp·(T−dd).</>
            ) : (
              <>Формула legacy: <span className="tabular-nums">U = C/W, E = A·(pp·D·pd − dp·D·dd), Adjusted = A + U + E</span>.</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
